import { RaidEntity } from "../types";

export const isBoss = (entity: Pick<RaidEntity, "entity_type">) =>
  ["BOSS", "BOSS_DOUBLE", "BOSS_PHASE"].includes(
    entity.entity_type.toUpperCase(),
  );
export const entityCategory = (entity: Pick<RaidEntity, "entity_type">) =>
  isBoss(entity)
    ? "보스"
    : entity.entity_type.toUpperCase() === "SUMMON"
      ? "쫄몹"
      : "오브젝트";

export interface RaidRoom {
  raid_code: string;
  room_code: string;
  room_order: number;
  representative_codes: string[];
}

export function validateRoom(room: RaidRoom, entities: RaidEntity[]) {
  if (!room.representative_codes.length)
    return "룸에는 대표 보스가 한 명 이상 필요합니다.";
  for (const code of room.representative_codes) {
    const entity = entities.find((e) => e.entity_code === code);
    if (!entity || !isBoss(entity))
      return "대표 몬스터는 보스 캐릭터만 지정할 수 있습니다.";
    if (entity.spawn_raid_code !== room.raid_code)
      return "같은 레이드에 속한 보스만 지정할 수 있습니다.";
  }
  return null;
}

// Keep deployed parameter names when editing historical records. Never leave
// two aliases pointing at different resources.
export const referenceKeys = {
  skill: ["skill_code", "skill_id", "skill", "skill_key"],
  effect: ["effect_code", "effect_id", "effect", "effect_key"],
  property: ["property", "property_name"],
};
export function readReference(
  params: any,
  kind: keyof typeof referenceKeys,
): string | undefined {
  return referenceKeys[kind]
    .map((k) => params?.[k])
    .find((v) => typeof v === "string" && v.length > 0);
}
export function writeReference(
  params: any,
  kind: keyof typeof referenceKeys,
  value: string,
) {
  const next = { ...params };
  const key =
    referenceKeys[kind].find((k) =>
      Object.prototype.hasOwnProperty.call(next, k),
    ) || referenceKeys[kind][0];
  referenceKeys[kind].forEach((k) => {
    delete next[k];
  });
  if (value) next[key] = value;
  return next;
}

export function skillReferences(trigger: any, actions: any[]): string[] {
  return Array.from(
    new Set(
      [
        ...(trigger.event_type?.startsWith("ON_SKILL_")
          ? [readReference(trigger.event_params, "skill")]
          : []),
        ...actions
          .filter((a) =>
            [
              "CAST_SKILL",
              "ADD_SKILL",
              "REMOVE_SKILL",
              "REPLACE_SKILL",
            ].includes(a.action_type),
          )
          .flatMap((a) => [
            readReference(a.action_params, "skill"),
            a.action_params?.from_skill,
            a.action_params?.to_skill,
          ]),
      ].filter(Boolean),
    ),
  ) as string[];
}

export function flattenConditionTree(
  root: any,
  uuid: () => string = () => crypto.randomUUID(),
) {
  const groups: any[] = [],
    conditions: any[] = [];
  const visit = (node: any): string => {
    const id = uuid();
    groups.push({
      id,
      group_name: node.group_name,
      logic_operator: node.logic_operator,
    });
    node.children.forEach((child: any, index: number) => {
      if (child.type === "GROUP") {
        conditions.push({
          id: uuid(),
          group_id: id,
          nested_group_id: visit(child),
          sort_order: index + 1,
        });
      } else {
        const { type, id: oldId, ...fields } = child;
        conditions.push({
          ...fields,
          id: uuid(),
          group_id: id,
          sort_order: index + 1,
        });
      }
    });
    return id;
  };
  visit(root);
  return { groups, conditions };
}

export function buildConditionTree(groups: any[], conditions: any[]) {
  if (!groups.length) return null;
  const referenced = new Set(
    conditions.map((c) => c.nested_group_id).filter(Boolean),
  );
  const roots = groups.filter((g) => !referenced.has(g.id));
  const seen = new Set<string>();
  const visit = (group: any): any => {
    if (!group || seen.has(group.id))
      throw new Error("조건 그룹에 순환 또는 잘못된 참조가 있습니다.");
    seen.add(group.id);
    return {
      ...group,
      type: "GROUP",
      children: conditions
        .filter((c) => c.group_id === group.id)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((c) =>
          c.nested_group_id
            ? visit(groups.find((g) => g.id === c.nested_group_id))
            : { ...c, type: "CONDITION" },
        ),
    };
  };
  if (!roots.length) throw new Error("조건 그룹의 루트를 찾을 수 없습니다.");
  const children = roots.map(visit);
  if (seen.size !== groups.length)
    throw new Error("연결되지 않은 조건 그룹이 있습니다.");
  // Older triggers can contain multiple top-level AND groups. Preserve all.
  return children.length === 1
    ? children[0]
    : {
        id: "root",
        type: "GROUP",
        group_name: "전체 조건",
        logic_operator: "AND",
        children,
      };
}
